import dayjs, { Dayjs } from 'dayjs';

import {
  booleanOptions,
  businessTypeOptions,
  coloursOptions,
  conditionsExpertiseOptions,
  detentionOptions,
  documentationOptions,
  duplicateKeysOptions,
  fuelOptions,
  genreOptions,
  mechanicalConditionOptions,
  testDriveOptions,
  transmissionOptions,
  vehicleOriginOptions,
  vehicleTypeOptions,
  vehicleUseOptions,
  options as makeOptions,
} from '../constants/cotation';

/**
 * Find an option by value from an options array
 * Handles case-insensitive matching for better compatibility
 */
const findOption = (
  options: readonly { value: string; label: string }[],
  value: string | null | undefined,
) => {
  if (!value) return undefined;

  // Try exact match first
  const exactMatch = options.find((opt) => opt.value === value);
  if (exactMatch) return { value: exactMatch.value, label: exactMatch.label };

  // Try case-insensitive match
  const caseInsensitiveMatch = options.find(
    (opt) => opt.value.toLowerCase() === value.toLowerCase(),
  );
  if (caseInsensitiveMatch) return { value: caseInsensitiveMatch.value, label: caseInsensitiveMatch.label };

  // Try uppercase for transmission (DB stores 'at', options have 'AT')
  const upperMatch = options.find((opt) => opt.value === value.toUpperCase());
  if (upperMatch) return { value: upperMatch.value, label: upperMatch.label };

  // Return a default object with the value
  return { value, label: value };
};

/**
 * Parse the damages JSON string into display texts
 * Handles multiple JSON stringification levels and various formats
 * Format: '["Pare-brise AV : Impact (150€)", "Pneu AVG : Usé (80€)"]'
 */
export const parseDamagesForDisplay = (damages: string | null): string[] => {
  if (!damages) return [];

  // Recursively parse JSON (handles double/triple stringification)
  const parseRecursively = (data: unknown, depth = 0): unknown => {
    if (depth >= 3 || typeof data !== 'string') return data;
    try {
      return parseRecursively(JSON.parse(data), depth + 1);
    } catch {
      return data;
    }
  };

  const parsedData = parseRecursively(damages);

  // If we got an array, filter and return valid strings
  if (Array.isArray(parsedData)) {
    return parsedData
      .filter((item: unknown) => typeof item === 'string' && item.trim() !== '')
      .map((item: string) => formatDamageText(item));
  }

  // If it's still a string, try splitting by newlines (old format)
  if (typeof parsedData === 'string') {
    const trimmed = parsedData.trim();
    if (trimmed.includes('\\n')) {
      return trimmed
        .split(/\\n/)
        .map((d) => d.replace(/\\+$/, '').trim())
        .filter((d) => d !== '')
        .map((item) => formatDamageText(item));
    }
    return trimmed ? [formatDamageText(trimmed)] : [];
  }

  return [];
};

/**
 * Format a damage text for display
 * Converts "pneu_ard : etat conforme (0 €)" to "Pneu ARD : Etat conforme (0 €)"
 */
const formatDamageText = (damage: string): string => {
  if (!damage) return '';

  const parts = damage.split(':');
  if (parts.length < 2) return damage;

  const partName = parts[0].trim();
  const description = parts.slice(1).join(':').trim();

  // Format the part name: replace underscores with spaces and capitalize
  const formattedPartName = partName
    .split('_')
    .map((word) => {
      // Keep position indicators (avg, avd, ard, arg, av, ar) uppercase
      if (['avg', 'avd', 'ard', 'arg', 'av', 'ar'].includes(word.toLowerCase())) {
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');

  // Capitalize first letter of description
  const formattedDescription = description.charAt(0).toUpperCase() + description.slice(1);

  return `${formattedPartName} : ${formattedDescription}`;
};

/**
 * Parse date string to dayjs object
 * Handles various formats: YYYY-MM-DD, DD-MM-YYYY, DD/MM/YYYY
 * Returns dayjs object for MUI DateField compatibility
 */
const parseDate = (dateStr: string | null | undefined): Dayjs | null => {
  if (!dateStr) return null;

  // Try ISO format first (YYYY-MM-DD) - dayjs handles this natively
  const parsed = dayjs(dateStr);
  if (parsed.isValid()) return parsed;

  // Try DD-MM-YYYY or DD/MM/YYYY format
  const parts = dateStr.split(/[-/]/);
  if (parts.length === 3) {
    // Check if first part is day (1-31) or year (> 31)
    const altParsed =
      parseInt(parts[0]) > 31
        ? dayjs(`${parts[0]}-${parts[1]}-${parts[2]}`) // YYYY-MM-DD format
        : dayjs(`${parts[2]}-${parts[1]}-${parts[0]}`); // DD-MM-YYYY format -> convert to YYYY-MM-DD

    if (altParsed.isValid()) return altParsed;
  }

  return null;
};

/**
 * Map database deal data to CreateCotation form data format
 */
export const mapResendDataToFormData = (resendData: any) => {
  return {
    // Vehicle identification
    reg_plate: resendData.reg_plate || '',
    vin: resendData.vin || '',

    // Vehicle details with proper option mapping
    genre: findOption(genreOptions, resendData.genre),
    reg_date: parseDate(resendData.reg_date),
    fuel: findOption(fuelOptions, resendData.fuel),
    make: findOption(makeOptions, resendData.make),
    model: resendData.model || '',
    derivative: resendData.derivative || '',
    commercial_finishing: resendData.commercial_finishing || '',
    transmission: findOption(transmissionOptions, resendData.transmission),
    colour: findOption(coloursOptions, resendData.colour),
    seats: resendData.number_of_seats?.toString() || '',
    doors: resendData.number_of_doors?.toString() || '',
    kms: resendData.kms?.toString() || '',
    options: resendData.options || '',

    // Documentation and revision
    documentation: findOption(documentationOptions, resendData.documentation),
    date_last_revision: parseDate(resendData.date_last_revision),
    kms_last_revision: resendData.kms_last_revision || null,
    duplicate_keys: findOption(duplicateKeysOptions, resendData.duplicate_keys),

    // Pricing
    desired_price: resendData.desired_price || '',
    market_price: resendData.market_price || '',

    // Vehicle status
    vat: findOption(booleanOptions, resendData.vat === 1 || resendData.vat === true ? 'yes' : 'no'),
    vehicle_origin: findOption(vehicleOriginOptions, resendData.source),
    vehicle_use: findOption(vehicleUseOptions, resendData.vehicle_usage),
    business_type: findOption(businessTypeOptions, resendData.business_type),
    detention: findOption(detentionOptions, resendData.detention_duration),

    // Inspection details
    histovec_link: resendData.histovec_link || '',
    test_drive: findOption(testDriveOptions, resendData.test_drive),
    mechanical_condition: findOption(mechanicalConditionOptions, resendData.mechanical_condition),
    conditions_expertise: findOption(conditionsExpertiseOptions, resendData.conditions_expertise),
    vehicle_type: findOption(vehicleTypeOptions, resendData.vehicle_type),
    kms_dial: findOption(booleanOptions, resendData.kms_confirm === 1 || resendData.kms_confirm === true ? 'yes' : 'no'),

    // Location and comments
    location: resendData.location || '',
    comp_infos: resendData.comp_infos_1 || '',
    comp_infos2: resendData.comp_infos_2 || '',
    more_api_infos: resendData.more_api_infos || '',

    // Damages - critical data
    damages: resendData.damages || '',
    damages_cost: resendData.damages_cost || 0,

    // Contact info for prospection
    first_name: resendData.first_name || '',
    last_name: resendData.last_name || '',
    phone_number: resendData.phone_number || '',
    email: resendData.email || '',
    sales_person_contact: resendData.sales_person_contact || '',
    seller_email: resendData.seller_email || '',
  };
};

/**
 * Map photos from database format to uploadedUrls format
 * Database format: { "frontRegPlate": ["url1"], "backRegPlate": ["url2"], ... }
 */
export const mapPhotosToUploadedUrls = (photos: any): { [key: string]: string[] } => {
  if (!photos) return {};

  // If it's already an object, return it
  if (typeof photos === 'object' && !Array.isArray(photos)) {
    return photos;
  }

  // If it's a JSON string, parse it
  if (typeof photos === 'string') {
    try {
      return JSON.parse(photos);
    } catch {
      return {};
    }
  }

  return {};
};
